/**
 * @fileoverview System document template builder — section + canvas editor for library templates.
 * @module system-web/pages/reports/SystemDocumentBuilderPage
 *
 * Responsibilities: - Provide the system-level (library) WYSIWYG editor for inspection report,
 *                     permit, and NOV document templates — the architectural twin of tenant-web's
 *                     DocumentTemplateBuilderPage, differing only in the system Visibility field.
 *                   - Offer the curated start-from-template gallery, scratch type selector, and
 *                     AI/PDF recreate entry points for a new template.
 *                   - Render a tabbed right pane (Live Preview | Properties): selecting a band
 *                     portals the canvas builder's Properties panel into the Properties tab at full
 *                     pane width (mirrors tenant-web) instead of the old fixed-width overlay.
 * Dependencies:     - @firesquirrel/ui-admin: DocumentLayoutHtmlPreview, DocumentCanvasBuilder,
 *                     DocumentTemplateGallery, DocumentTypeSelector, section editors, help/tour.
 *                   - @firesquirrel/document-render: documentConfigToLayout, applyDocumentOps, DocumentLayout.
 *                   - systemReportTemplateService (Dexie read/write); systemDocumentAiService.
 *                   - @firesquirrel/local-db: ReportTemplateLocal + builder<->config mappers.
 * Architecture:     - Local-first: edits persisted to Dexie first, server sync in background.
 *                     Uses the SAME shared packages as tenant-web (DRY); system-only difference is
 *                     library Visibility and the system Dexie binding.
 *                   - Route: /document-templates/:id (":id" === "new" for creation).
 * Notes:            - DocumentConfig (legacy) templates auto-migrate to DocumentLayout on first open.
 */

// ============================================================
// System Document Template Builder Page — Enhanced Editor
// Full section editor for system-level document templates
// Synced with tenant-web DocumentTemplateBuilderPage (15 sections)
// Route: /document-templates/:id
// ============================================================

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import {
  ArrowLeft, Eye, FileText, Settings, Settings2, Layout, Image, PenTool,
  Scale, AlertTriangle, Camera, StickyNote, ChevronRight, Plus, Trash2,
  GripVertical, Check, X, User, MapPin, Type, MessageSquare, Phone,
  Paperclip, CalendarDays, ClipboardList, Sparkles, Upload,
  PanelRightOpen, PanelRightClose, HelpCircle, Compass,
} from 'lucide-react';
import { useTheme, themeCSS } from '@ui-admin/contexts/ThemeContext';
import type { ReportTemplateLocal } from '@firesquirrel/local-db';
import { builderConfigToReportTemplate, reportTemplateToBuilderConfig } from '@firesquirrel/local-db';
import { getReportTemplate, updateReportTemplate, createReportTemplate, publishReportTemplate, archiveReportTemplate } from '../../services/systemReportTemplateService';
import { watchTenants } from '../../services/tenantService';
import { telemetry } from '../../services/telemetry';
import { DocumentLayoutHtmlPreview } from '@firesquirrel/ui-admin';
import { DocumentTemplateGallery, DocumentCanvasBuilder, RichTextEditor, PageSetupPanel, AccordionSection, RecreatePlaceholderReviewModal, DocumentLifecycleControls } from '@firesquirrel/ui-admin';
import { DocumentTypeSelector } from '@firesquirrel/ui-admin';
import type { DocumentTypeSelectorValue } from '@firesquirrel/ui-admin';
import {
  BuilderHelpPanel, GuidedTour, SectionHelpPopover,
  SECTIONS_TOUR_STEPS, SECTIONS_TOUR_TARGET, SECTION_HELP,
} from '@firesquirrel/ui-admin';
import type { HelpSectionId } from '@firesquirrel/ui-admin';
// Shared document section editors + builder-config model (extracted to @firesquirrel/ui-admin,
// G-DRY-01). Rendered by both the Section list and the Canvas selection panel (G-PARITY-01/02).
// Importing these restores the system-web Color Theme picker (G-THEME-SW-01) and the cover-page
// editor automatically — both apps now render the one shared set.
import {
  AdvancedDataEditor, DocumentThemeEditor, ReportDataScopeControl, applyReportDataScopeToDocumentConfig,
  DEFAULT_CONFIG, DEFAULT_SECTIONS, SECTION_ICONS,
} from '@firesquirrel/ui-admin';
import type { DocumentConfig, SectionId, SectionConfig } from '@firesquirrel/ui-admin';
import type { FieldCatalog, FieldCatalogEntry, AdvancedFieldPlacement, PreviewDepartmentSource } from '@firesquirrel/core-domain';
import { buildFieldCatalog, DOCUMENT_TEMPLATE_PRESETS, resolveDocumentPreviewData, buildPreviewDataSource, buildSnapshotFromCatalog, buildBrandedPreviewDataSource, collectDepartmentLogoIds, resolvePreviewDepartmentLogoUrl, resolvePreviewDepartmentSecondLogoUrl } from '@firesquirrel/core-domain';
import { useResolvedLogoUrls } from '@firesquirrel/file-service-client/hooks';
import type { TemplateSnapshotInput } from '@firesquirrel/core-domain';
import { documentConfigToLayout, applyDocumentOps, collectUploadedLogoIds, renamePlaceholdersInLayout, bandActivationList, setBandEnabled } from '@firesquirrel/document-render';
import type { DocumentLayout } from '@firesquirrel/document-render';
import { recreateDocumentFromUpload, askDocumentAgent } from '../../services/systemDocumentAiService';
import { getUploadedFileDownloadUrl } from '../../services/systemUploadedFileService';
import FileUploadModal from '../../components/FileUploadModal';
import type { UploadedFileLocal } from '@firesquirrel/local-db';

/**
 * Persist a numeric layout preference (panel width / preview split) to localStorage (H2/H3).
 * @hook usePersistedNumber
 * @storage localStorage key (e.g. doc-builder-panel-width); ephemeral UI preference only.
 * @errorHandling Falls back to `fallback` when storage is unavailable or the value is non-finite.
 */
function usePersistedNumber(key: string, fallback: number): [number, (n: number) => void] {
  const [value, setValue] = useState<number>(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
      const parsed = raw == null ? NaN : Number(raw);
      return Number.isFinite(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  });
  const set = useCallback((n: number) => {
    setValue(n);
    try { window.localStorage.setItem(key, String(n)); } catch { /* ignore quota/availability */ }
  }, [key]);
  return [value, set];
}

// ============================================================
// Local constants
// (Builder-config types + section editors are imported from @firesquirrel/ui-admin — G-DRY-01.)
// ============================================================

const SAMPLE_PREVIEW_DEPARTMENT_ID = '__sample_preview_department__';

// ============================================================
// Main Component
// ============================================================

interface DocumentTemplateBuilderPageProps {
  mode?: 'edit' | 'view';
}

export default function DocumentTemplateBuilderPage({ mode = 'edit' }: DocumentTemplateBuilderPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const method = searchParams.get('method') ?? null;
  const { setBreadcrumbLabel } = useBreadcrumbs();
  const { theme, themeId } = useTheme();
  const themeCssString = useMemo(() => themeCSS(theme), [theme]);
  const isNew = !id || id === 'new';
  const isViewMode = mode === 'view';

  // Check for AI-generated config passed via navigation state (from chatbot "Open in Builder")
  const aiNavState = location.state as { aiGenerated?: boolean; previewConfig?: any } | null;
  const [isAIGenerated, setIsAIGenerated] = useState<boolean>(aiNavState?.aiGenerated || false);

  const [definition, setDefinition] = useState<ReportTemplateLocal | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [templateName, setTemplateName] = useState('Untitled Document Template');
  const [templateDescription, setTemplateDescription] = useState('');
  const [config, setConfig] = useState<DocumentConfig>(DEFAULT_CONFIG);
  /** Single-canvas: the band selected in BOTH the left activation list and the canvas (shared). */
  const [selectedBandId, setSelectedBandId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showPageSetup, setShowPageSetup] = useState(false);
  // Phase 2 H2/H3: resizable left settings panel + preview split (persisted) and preview collapse.
  const [leftPanelWidth, setLeftPanelWidth] = usePersistedNumber('doc-builder-panel-width', 256);
  const [previewWidth, setPreviewWidth] = usePersistedNumber('doc-builder-preview-split', 384);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  // Right-pane tabs: the Live Preview and the band Properties share one resizable pane and are shown
  // alternately (selecting an element switches to Properties; the user can toggle back to Preview).
  // This replaces the old fixed-width Properties overlay that crushed the rich-text editor.
  const [rightTab, setRightTab] = useState<'preview' | 'properties'>('preview');
  // DOM container the canvas builder portals its Properties panel into (the Properties tab body).
  const [propertiesHost, setPropertiesHost] = useState<HTMLDivElement | null>(null);

  // Selecting a band (from the activation list or the canvas) reveals its Properties tab and expands
  // the pane if collapsed; clearing the selection returns the pane to the Live Preview tab. This runs
  // on every selection event — not just when the id changes — so re-clicking an already-selected band
  // re-activates the Properties tab even if the user had manually switched back to Live Preview.
  const handleSelectBand = useCallback((id: string | null) => {
    setSelectedBandId(id);
    if (id) {
      setRightTab('properties');
      setPreviewCollapsed(false);
    } else {
      setRightTab('preview');
    }
  }, []);
  const resizeRef = useRef<null | { kind: 'left' | 'preview'; startX: number; startWidth: number }>(null);

  // Drag-to-resize for the left settings panel and the preview pane (H2/H3).
  const beginResize = useCallback((kind: 'left' | 'preview', startWidth: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    // Capture the pointer so events continue routing here even when the pointer moves
    // over a child iframe. Without capture the iframe swallows pointermove/pointerup
    // and the drag state sticks permanently.
    e.currentTarget.setPointerCapture(e.pointerId);
    resizeRef.current = { kind, startX: e.clientX, startWidth };
    const onMove = (ev: PointerEvent) => {
      const st = resizeRef.current;
      if (!st) return;
      const delta = st.kind === 'left' ? ev.clientX - st.startX : st.startX - ev.clientX;
      const raw = st.startWidth + delta;
      if (st.kind === 'left') {
        setLeftPanelWidth(Math.max(200, Math.min(440, raw)));
      } else if (raw < 200) {
        setPreviewCollapsed(true);
      } else {
        setPreviewCollapsed(false);
        setPreviewWidth(Math.max(260, Math.min(760, raw)));
      }
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [setLeftPanelWidth, setPreviewWidth]);

  // Ctrl+Shift+P toggles the preview pane between collapsed and last-used width (H2).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'P' || e.key === 'p')) {
        e.preventDefault();
        setPreviewCollapsed((c) => !c);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Advanced Linked Document state
  const [advancedMode, setAdvancedMode] = useState<'standard' | 'advancedLinked'>('standard');
  const [linkedTemplateId, setLinkedTemplateId] = useState<string | undefined>();
  const [linkedTemplateName, setLinkedTemplateName] = useState<string | undefined>();
  const [advancedFields, setAdvancedFields] = useState<AdvancedFieldPlacement[]>([]);
  const [fieldCatalog, setFieldCatalog] = useState<FieldCatalog | null>(null);
  const [showAdvancedEditor, setShowAdvancedEditor] = useState(false);
  const [showAdvancedTab, setShowAdvancedTab] = useState(false);
  const [inspectionTemplates, setInspectionTemplates] = useState<Array<{ _id: string; name: string }>>([]);
  /** Whether the auto-appended "Inspection Data" table renders at the bottom of the PDF. */
  const [renderAdvancedSection, setRenderAdvancedSection] = useState<boolean>(true);
  /** Custom heading for the advanced-fields bottom table. Defaults to "Inspection Data". */
  const [advancedSectionLabel, setAdvancedSectionLabel] = useState<string>('Inspection Data');
  /** Per-field copy-token feedback: fieldId → 'copied' | undefined */
  const [copiedFieldId, setCopiedFieldId] = useState<string | null>(null);
  // Phase 2 (E3): show the curated "start from template" gallery for a brand-new document.
  // The gallery is the default landing for a new template (including a bare /new URL); it is
  // skipped only when the author explicitly chose another creation method (scratch → type
  // selector, ai → PDF upload) or when an AI-generated config pre-populates the builder.
  const [showGallery, setShowGallery] = useState<boolean>(
    isNew && method !== 'scratch' && method !== 'ai' && !aiNavState?.previewConfig,
  );
  const [showTypeSelector, setShowTypeSelector] = useState<boolean>(isNew && method === 'scratch');
  // Unified single-canvas experience: the band/flow canvas is the only editing surface. `editMode`
  // is retained (always 'canvas') only to gate canvas-specific chrome (Page Setup, Ask Squirrel).
  const [editMode, setEditMode] = useState<'sections' | 'canvas'>('canvas');
  const [documentLayout, setDocumentLayout] = useState<DocumentLayout | null>(null);
  // Phase 1 (G-LOGO-01..03): uploaded-logo picker bridge + resolved blob URLs for thumbnail/preview.
  const [uploadedLogoUrls, setUploadedLogoUrls] = useState<Record<string, string>>({});
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);
  const logoPickerResolveRef = useRef<((id: string | null) => void) | null>(null);
  const [recreatedFrom, setRecreatedFrom] = useState<{
    fileName: string; sizeBytes: number; sha256: string;
    parser: 'llamaparse' | 'local'; recreatedBy: string; recreatedAt: string;
  } | null>(null);
  const [recreating, setRecreating] = useState(false);
  // G-AI-UX-01: confirm-and-bind review of detected merge-field placeholders (replaces window.alert).
  const [placeholderReview, setPlaceholderReview] = useState<{ fileName: string; placeholders: string[] } | null>(null);
  const recreateInputRef = useRef<HTMLInputElement>(null);
  // Open PDF upload immediately when navigated with method=ai.
  useEffect(() => {
    if (isNew && method === 'ai' && recreateInputRef.current) {
      recreateInputRef.current.click();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [squirrelInput, setSquirrelInput] = useState('');
  const [squirrelBusy, setSquirrelBusy] = useState(false);
  const [squirrelReply, setSquirrelReply] = useState('');

  // Phase 5 (Section editor): illustrated help panel, guided tour, and per-section popover.
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpPanelSection, setHelpPanelSection] = useState<HelpSectionId | undefined>(undefined);
  const [tourActive, setTourActive] = useState(false);
  const [helpSectionId, setHelpSectionId] = useState<SectionId | null>(null);
  const sectionHelpAnchorRef = useRef<HTMLButtonElement | null>(null);

  const openHelpAt = useCallback((section?: HelpSectionId) => {
    setHelpPanelSection(section);
    setHelpOpen(true);
  }, []);
  const startSectionsTour = useCallback(() => {
    setHelpSectionId(null);
    setHelpOpen(false);
    setTourActive(true);
  }, []);
  const finishTour = useCallback(() => {
    setTourActive(false);
  }, []);
  const openSectionHelp = useCallback((id: SectionId, el: HTMLButtonElement) => {
    sectionHelpAnchorRef.current = el;
    setHelpSectionId((cur) => (cur === id ? null : id));
  }, []);

  /** Add an inspection field to the advanced placements (in-memory builder state; persisted on save). */
  const handleAddFieldPlacement = useCallback((entry: FieldCatalogEntry) => {
    const placement: AdvancedFieldPlacement = {
      id: `af-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      label: entry.label,
      sourceType: 'templateResponse',
      bindingKey: entry.bindingKey,
      fieldId: entry.fieldId,
      formId: entry.formId,
      formName: entry.formName,
      fieldLabel: entry.label,
      renderMode: entry.type === 'boolean' || entry.type === 'toggle' ? 'checkbox'
        : entry.type === 'photo' ? 'photo'
        : 'inlineText',
      includeWhenEmpty: true,
    };
    setAdvancedFields(prev => [...prev, placement]);
  }, []);

  const handleRemoveFieldPlacement = useCallback((placementId: string) => {
    setAdvancedFields(prev => prev.filter(f => f.id !== placementId));
  }, []);

  /** Apply a curated preset's config factory, then dismiss the gallery. */
  const applyPreset = useCallback((presetId: string) => {
    const preset = DOCUMENT_TEMPLATE_PRESETS.find((p) => p.id === presetId);
    if (preset) {
      setConfig({ ...DEFAULT_CONFIG, ...(preset.factory() as Partial<DocumentConfig>) } as DocumentConfig);
      if (!templateName || templateName === 'Untitled Document Template') setTemplateName(preset.name);
    }
    setShowGallery(false);
  }, [templateName]);

  // Phase AI: canvas mode + AI recreate + Squirrel document-agent handlers.
  const buildCanvasLayout = useCallback((): DocumentLayout => {
    const serializable = { ...config, sections: config.sections.map(({ icon, ...rest }: any) => rest) };
    return documentConfigToLayout(serializable as any);
  }, [config]);

  /**
   * Single-canvas source of truth: commit a new DocumentLayout (from the canvas OR the left
   * activation list) and mark the layout dirty so Save persists it.
   * @process updateLayout
   * Steps:   setDocumentLayout(next).
   * Inputs:  the next DocumentLayout.
   * Outputs: layout state updated; canvas + activation list re-render from it.
   * Failure Handling: none (pure state set).
   * Idempotency: REQUIRED — repeated identical layouts are harmless.
   * @localFirst Source of Truth: documentLayout (persisted to the Dexie-backed reportTemplate on Save).
   *   Write Flow: optimistic in-memory update now; durable on Save → systemReportTemplateService → Dexie.
   *   Sync Strategy: the worker renderer prefers documentLayout over config.sections.
   */
  const updateLayout = useCallback((next: DocumentLayout) => {
    setDocumentLayout(next);
  }, []);

  /**
   * Toggle a band on/off from the left activation checklist. Only enabled bands render on the
   * canvas + preview (and in the printed PDF).
   * @process toggleBandActivation
   * Idempotency: REQUIRED — derived from current layout each call.
   */
  const toggleBandActivation = useCallback((bandId: string, enabled: boolean) => {
    const base = documentLayout ?? buildCanvasLayout();
    updateLayout(setBandEnabled(base, bandId, enabled));
  }, [documentLayout, buildCanvasLayout, updateLayout]);

  // Initialize the canvas layout once the builder is ready (new template after type selection, or
  // a loaded legacy template with no persisted layout). After this, documentLayout is the single
  // structural source of truth — never regenerated from config behind the user's back.
  useEffect(() => {
    if (loading || showTypeSelector) return;
    // Functional update so a concurrent user edit (e.g. an activation toggle made in the brief
    // frame after `loading` clears but before this commits) is never clobbered by re-initialization.
    setDocumentLayout((prev) => prev ?? buildCanvasLayout());
  }, [loading, showTypeSelector, buildCanvasLayout]);

  const handleRecreateFile = useCallback(async (file: File | null) => {
    if (!file) return;
    setRecreating(true);
    try {
      const result = await recreateDocumentFromUpload(file);
      setDocumentLayout(result.layout as unknown as DocumentLayout);
      setRecreatedFrom({
        fileName: result.source.fileName,
        sizeBytes: result.source.sizeBytes,
        sha256: result.source.sha256,
        parser: result.parser,
        recreatedBy: 'system-admin',
        recreatedAt: new Date().toISOString(),
      });
      setEditMode('canvas');
      if (result.detectedPlaceholders.length > 0) {
        setPlaceholderReview({ fileName: file.name, placeholders: result.detectedPlaceholders });
      }
    } catch (err) {
      window.alert(`Recreate failed: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setRecreating(false);
      if (recreateInputRef.current) recreateInputRef.current.value = '';
    }
  }, []);

  const handleAskSquirrel = useCallback(async () => {
    const message = squirrelInput.trim();
    if (!message) return;
    setSquirrelBusy(true);
    setSquirrelReply('');
    try {
      const base = documentLayout ?? buildCanvasLayout();
      const { operations, message: reply } = await askDocumentAgent(message, base);
      setDocumentLayout(applyDocumentOps(base, operations as any));
      setEditMode('canvas');
      setSquirrelReply(reply || `Applied ${Array.isArray(operations) ? operations.length : 0} change(s).`);
      setSquirrelInput('');
    } catch (err) {
      setSquirrelReply(`Squirrel could not help: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setSquirrelBusy(false);
    }
  }, [squirrelInput, documentLayout, buildCanvasLayout]);

  // Active departments available to drive the preview (system-web has no implicit tenant).
  const [previewDepartments, setPreviewDepartments] = useState<PreviewDepartmentSource[]>([]);
  const [previewDepartmentId, setPreviewDepartmentId] = useState<string>(SAMPLE_PREVIEW_DEPARTMENT_ID);

  useEffect(() => {
    const sub = watchTenants().subscribe({
      next: (rows) => {
        const active = (rows as PreviewDepartmentSource[])
          .filter((t) => t.status === 'active')
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setPreviewDepartments(active);
      },
      error: () => setPreviewDepartments([]),
    });
    return () => sub.unsubscribe();
  }, []);

  // Preview data source: the selected department drives the preview; with none selected the
  // shared builder falls back to SAMPLE_PREVIEW_DATA. tenant-web uses this same builder with its
  // implicit current department, so both apps share one process.
  const previewDataSource = useMemo(
    () => buildPreviewDataSource(
      previewDepartmentId === SAMPLE_PREVIEW_DEPARTMENT_ID
        ? null
        : previewDepartments.find((d) => String(d._id) === previewDepartmentId),
    ),
    [previewDepartments, previewDepartmentId],
  );

  // G-LOGO-DEPT: resolve department logo uploadedFileIds to SAS URLs and patch the preview
  // data source so raw Azure blobUrls are replaced with displayable URLs. Both the live
  // preview and sidebar thumbnails consume brandedPreviewDataSource instead of the raw one.
  const deptLogoUrls = useResolvedLogoUrls(previewDataSource, collectDepartmentLogoIds);
  const brandedPreviewDataSource = useMemo(
    () => buildBrandedPreviewDataSource(previewDataSource, deptLogoUrls),
    [previewDataSource, deptLogoUrls],
  );

  // Option 3: inject fieldCatalog-based advanced block so {{advField:...}} tokens resolve
  // to ☐ (unchecked default) in the preview, matching PDF output (G-DRY-RENDER parity).
  const canvasSampleData = useMemo(() => {
    const previewSource = fieldCatalog && advancedFields.length > 0
      ? { ...brandedPreviewDataSource, advanced: { responses: {}, templateSnapshot: buildSnapshotFromCatalog(fieldCatalog) } }
      : brandedPreviewDataSource;
    // Pass the live documentLayout so band activation (band.enabled) gates content resolution —
    // checking an element in the activation list now resolves its content (Finding 1). The shared
    // core applies the canvas letterhead logo-slot override itself (G-LOGO-05) via
    // resolveUploadedFileUrl, so the live preview no longer needs a separate applyLayoutHeaderLogos
    // post-processing step here.
    return resolveDocumentPreviewData(
      previewSource,
      config as any,
      advancedFields,
      documentLayout ?? undefined,
      (fid: string) => uploadedLogoUrls[fid],
    );
  }, [brandedPreviewDataSource, config, fieldCatalog, advancedFields, documentLayout, uploadedLogoUrls]);

  // G-LOGO-01: open the shared upload modal and resolve the picked UploadedFile id (or null).
  const handlePickLogo = useCallback((): Promise<string | null> => {
    return new Promise<string | null>((resolve) => {
      logoPickerResolveRef.current = resolve;
      setLogoPickerOpen(true);
    });
  }, []);

  const resolveLogoThumb = useCallback(
    (uploadedFileId: string): string | undefined => uploadedLogoUrls[uploadedFileId],
    [uploadedLogoUrls],
  );
  const resolveDepartmentLogoThumb = useCallback(
    // Shared helper: reads the effective department logo URL from the branded preview
    // data source (which patches raw Azure blobUrls with resolved SAS URLs).
    () => resolvePreviewDepartmentLogoUrl(brandedPreviewDataSource),
    [brandedPreviewDataSource],
  );
  const resolveDepartmentSecondLogoThumb = useCallback(
    () => resolvePreviewDepartmentSecondLogoUrl(brandedPreviewDataSource),
    [brandedPreviewDataSource],
  );

  const handleLogoUploaded = useCallback((file: UploadedFileLocal) => {
    setUploadedLogoUrls((prev) => ({ ...prev, [file._id]: file.blobUrl }));
    logoPickerResolveRef.current?.(file._id);
    logoPickerResolveRef.current = null;
    setLogoPickerOpen(false);
  }, []);

  const handleLogoPickerClose = useCallback(() => {
    logoPickerResolveRef.current?.(null);
    logoPickerResolveRef.current = null;
    setLogoPickerOpen(false);
  }, []);

  // G-LOGO-03: resolve uploaded-logo ids already referenced by the layout to their blob URLs
  // (Dexie read via the local-first systemUploadedFileService) so thumbnails/preview render after load.
  useEffect(() => {
    if (!documentLayout) return;
    const ids: string[] = collectUploadedLogoIds(documentLayout).filter((fid: string) => !(fid in uploadedLogoUrls));
    if (ids.length === 0) return;
    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        ids.map(async (fid: string) => [fid, await getUploadedFileDownloadUrl(fid)] as const),
      );
      if (cancelled) return;
      const resolved = entries.filter((e): e is readonly [string, string] => typeof e[1] === 'string');
      if (resolved.length > 0) {
        setUploadedLogoUrls((prev) => ({ ...prev, ...Object.fromEntries(resolved) }));
      }
    })();
    return () => { cancelled = true; };
  }, [documentLayout, uploadedLogoUrls]);

  useEffect(() => {
    if (!isNew && id) {
      loadTemplate(id);
    } else if (isNew && aiNavState?.previewConfig) {
      // Pre-populate builder with AI-generated document config from chatbot
      const preview = aiNavState.previewConfig;
      const docConfig = preview.documentConfig;
      console.log('[SystemDocumentBuilderPage] AI nav state received:', JSON.stringify(aiNavState, null, 2));
      console.log('[SystemDocumentBuilderPage] docConfig:', JSON.stringify(docConfig, null, 2));
      console.log('[SystemDocumentBuilderPage] logoPosition from AI:', docConfig?.logoPosition);
      // Mark as AI-generated
      setIsAIGenerated(true);
      if (preview.name) setTemplateName(preview.name);
      if (docConfig) {
        const patch: Partial<DocumentConfig> = {};
        // Apply layout
        if (docConfig.layout?.orientation) patch.orientation = docConfig.layout.orientation;
        if (docConfig.layout?.pageSize) patch.pageSize = docConfig.layout.pageSize;
        // Apply logo position from AI analysis
        if (docConfig.logoPosition || docConfig.detectedLogoArea) {
          patch.header = {
            ...DEFAULT_CONFIG.header!,
            showLogo: true,
            logoPosition: docConfig.logoPosition || DEFAULT_CONFIG.header!.logoPosition,
          };
        }

        // Apply document structure elements from AI
        const ds = docConfig.documentStructure;
        if (ds) {
          console.log('[SystemDocumentBuilderPage] documentStructure:', JSON.stringify(ds, null, 2));

          // Build a comprehensive section enable map based on AI detection
          // Sections always enabled: header, footer (structural elements)
          // Sections enabled/disabled based on AI detection:
          const sectionEnableMap: Record<string, boolean> = {
            cover: false,
            header: true,
            recipientBlock: true,
            subjectLine: !!ds.subjectLine?.detected,
            salutation: !!ds.salutation?.detected,
            bodyText: !!(ds.bodyParagraphs && ds.bodyParagraphs.length > 0),
            metadata: true,
            violations: true,
            photos: !!ds.hasPhotos,
            comments: !!ds.hasComments,
            contactBlock: !!ds.contactBlock?.detected,
            signatures: !!ds.hasSignatures,
            legal: !!ds.hasLegalText,
            footer: true,
            staticContent: false,
          };

          patch.sections = DEFAULT_SECTIONS.map(s => ({
            ...s,
            enabled: sectionEnableMap[s.id] ?? s.enabled,
          }));

          // Apply subject line config
          if (ds.subjectLine?.detected) {
            patch.subjectLine = {
              prefix: ds.subjectLine.prefix || 'RE:',
              template: ds.subjectLine.text || '{{inspectionType}} Inspection',
            };
          }

          // Apply salutation config
          if (ds.salutation?.detected) {
            patch.salutation = {
              text: ds.salutation.text || 'Dear Property Owner(s),',
            };
          }

          // Apply body text paragraphs
          if (ds.bodyParagraphs && ds.bodyParagraphs.length > 0) {
            patch.bodyText = {
              paragraphs: ds.bodyParagraphs.map((p: any, idx: number) => ({
                id: `ai-para-${idx}`,
                content: p.text || '',
                condition: p.condition || 'always',
              })),
            };
          }

          // Apply contact block
          if (ds.contactBlock?.detected) {
            patch.contactBlock = {
              ...DEFAULT_CONFIG.contactBlock!,
              introText: ds.contactBlock.text || DEFAULT_CONFIG.contactBlock!.introText,
            };
          }
        }

        setConfig(prev => ({ ...prev, ...patch }));
        console.log('[SystemDocumentBuilderPage] Applied AI config:', { logoPosition: docConfig.logoPosition, layout: docConfig.layout, structureDetected: !!ds });
      }
    }
  }, [id, isNew]);

  async function loadTemplate(templateId: string) {
    setLoading(true);
    try {
      const data = await getReportTemplate(templateId);
      if (data) {
        setDefinition(data);
        setTemplateName(data.name);
        setTemplateDescription(data.description || '');
        // Transform flat reportTemplates schema back to builder DocumentConfig
        const loadedConfig = reportTemplateToBuilderConfig(data);
        // Restore icon references from SECTION_ICONS map
        if (loadedConfig.sections) {
          loadedConfig.sections = loadedConfig.sections.map((s: any) => ({
            ...s,
            icon: SECTION_ICONS[s.id as SectionId] || FileText,
          }));
        }
        setConfig({ ...DEFAULT_CONFIG, ...loadedConfig } as DocumentConfig);
        // Phase AI: restore canvas layout and recreate provenance if present.
        if ((data as any).documentLayout) setDocumentLayout((data as any).documentLayout as DocumentLayout);
        if ((data as any).recreatedFrom) setRecreatedFrom((data as any).recreatedFrom as any);
        // Restore advanced binding state
        if (data.advancedBinding?.mode === 'advancedLinked') {
          setAdvancedMode('advancedLinked');
          setLinkedTemplateId(data.advancedBinding.linkedInspectionTemplateId);
          setLinkedTemplateName(data.advancedBinding.linkedInspectionTemplateName);
          setShowAdvancedTab(true);
          setRenderAdvancedSection(data.advancedBinding.renderAdvancedSection !== false);
          setAdvancedSectionLabel(data.advancedBinding.advancedSectionLabel || 'Inspection Data');
        }
        if (data.advancedFields && data.advancedFields.length > 0) {
          setAdvancedFields(data.advancedFields as AdvancedFieldPlacement[]);
        }
        if (data.name) setBreadcrumbLabel(`/document-templates/${templateId}`, data.name);
        
        // Detect if this is an AI-generated template
        const isAITemplate = (data as any).aiGenerationSource === 'ai-generated';
        setIsAIGenerated(isAITemplate);
      }
    } catch (error) {
      console.error('[SystemDocumentBuilderPage] Failed to load template:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleSave = useCallback(async (): Promise<boolean> => {
    setSaving(true);
    try {
      // Strip React component icons from sections before transform
      const serializableConfig = {
        ...config,
        sections: config.sections.map(({ icon, ...rest }) => rest),
      };

      // Transform builder config → flat reportTemplates schema
      const specFields = builderConfigToReportTemplate(serializableConfig as any);

      const templateData: Record<string, any> = {
        ...specFields,
        name: templateName || 'Untitled Document Template',
        description: templateDescription,
        status: definition?.status || ('draft' as const),
        createdBy: definition?.createdBy || 'system-admin',
        updatedBy: 'system-admin',
        // Persist the full builder DocumentConfig so the worker's universal
        // renderer can use it (layoutMode, header, sections, etc.)
        documentConfig: serializableConfig,
        // Preserve AI generation marker
        ...(isAIGenerated && { aiGenerationSource: 'ai-generated' as const }),
        // Phase AI: persist canvas layout and source provenance (file itself is never stored).
        ...(documentLayout && { documentLayout }),
        ...(recreatedFrom && { recreatedFrom }),
      };

      // Persist advanced binding state
      if (advancedMode === 'advancedLinked' && linkedTemplateId) {
        templateData.advancedBinding = {
          mode: 'advancedLinked',
          linkedInspectionTemplateId: linkedTemplateId,
          linkedInspectionTemplateName: linkedTemplateName,
          fieldCatalogVersion: fieldCatalog?.fieldCount ?? 0,
          renderAdvancedSection,
          advancedSectionLabel: advancedSectionLabel || 'Inspection Data',
        };
        templateData.advancedFields = advancedFields;
      } else {
        templateData.advancedBinding = { mode: 'standard' };
        templateData.advancedFields = [];
      }

      if (isNew) {
        const newTemplate = await createReportTemplate(templateData as any);
        if (newTemplate) {
          navigate(`/document-templates/${newTemplate._id}`);
        }
      } else if (id) {
        const updated = await updateReportTemplate(id, templateData);
        if (updated) {
          setDefinition(updated);
        }
      }
      return true;
    } catch (error) {
      console.error('[SystemDocumentBuilderPage] Failed to save:', error);
      return false;
    } finally {
      setSaving(false);
    }
  }, [templateName, templateDescription, config, definition, isNew, id, navigate, advancedMode, linkedTemplateId, linkedTemplateName, fieldCatalog, advancedFields, isAIGenerated, documentLayout, recreatedFrom, renderAdvancedSection, advancedSectionLabel]);

  const handlePublish = useCallback(async () => {
    if (!id || isNew) return;
    const saved = await handleSave();
    if (!saved) return;
    setPublishing(true);
    try {
      const updated = await publishReportTemplate(id);
      if (updated) setDefinition(updated);
    } catch (error) {
      console.error('[SystemDocumentBuilderPage] Failed to publish:', error);
    } finally {
      setPublishing(false);
    }
  }, [id, isNew, handleSave]);

  const handleArchive = useCallback(async () => {
    if (!id || isNew) return;
    try {
      const updated = await archiveReportTemplate(id);
      if (updated) setDefinition(updated);
    } catch (error) {
      console.error('[SystemDocumentBuilderPage] Failed to archive:', error);
    }
  }, [id, isNew]);


  /** @logging Events: report_template_data_scope_changed; Level: info; Correlation: none; Properties: scope only. */
  const handleReportDataScopeChange = useCallback((next: DocumentConfig) => {
    telemetry.trackEvent('report_template_data_scope_changed', { scope: next.reportDataScope });
    setConfig(next);
  }, []);

  const handleDocumentTypeChange = useCallback((type: DocumentTypeSelectorValue) => {
    setConfig((prev) => {
      const next = { ...prev, documentType: type as import('@firesquirrel/local-db').DocumentType };
      return type !== 'inspectionReport' ? applyReportDataScopeToDocumentConfig(next, 'singleCase') : next;
    });
  }, []);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500">Loading template...</div>
      </div>
    );
  }

  // Full-screen preview modal
  if (showPreview) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-white">
        <header className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5 text-gray-500" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{templateName}</h2>
              <p className="text-sm text-gray-500">Document Template Preview</p>
            </div>
          </div>
          <button
            onClick={() => setShowPreview(false)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <X className="h-4 w-4" />
            Close Preview
          </button>
        </header>
        <main className="flex-1 overflow-auto p-8 bg-gray-100">
          <div className="max-w-3xl mx-auto">
            <DocumentLayoutHtmlPreview layout={documentLayout ?? buildCanvasLayout()} renderData={canvasSampleData} scale={1} />
          </div>
        </main>
      </div>
    );
  }

  // Phase 2 (F1): document type selector for new templates started from scratch.
  if (showTypeSelector) {
    return (
      <div className="h-full flex flex-col" style={{ background: theme.pageBg }} data-theme={themeId}>
        <header className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">New Document Template</h2>
          </div>
          <button
            onClick={() => navigate('/document-templates')}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            <X className="h-4 w-4" /> Cancel
          </button>
        </header>
        <main className="flex-1 overflow-auto">
          <DocumentTypeSelector
            onSelect={(type: DocumentTypeSelectorValue) => {
              handleDocumentTypeChange(type);
              setShowTypeSelector(false);
            }}
            onBack={() => navigate('/document-templates')}
          />
        </main>
      </div>
    );
  }

  // Phase 2 (E3): curated "start from template" gallery for a new document.
  if (showGallery) {
    return (
      <div className="h-full flex flex-col" style={{ background: theme.pageBg }} data-theme={themeId}>
        <header className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">New Document Template</h2>
          </div>
          <button
            onClick={() => navigate('/document-templates')}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            <X className="h-4 w-4" /> Cancel
          </button>
        </header>
        <main className="flex-1 overflow-auto p-8">
          <div className="max-w-4xl mx-auto">
            <DocumentTemplateGallery
              items={DOCUMENT_TEMPLATE_PRESETS.map((p) => ({ id: p.id, name: p.name, description: p.description, category: p.category }))}
              onSelect={applyPreset}
              onStartBlank={() => setShowGallery(false)}
            />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ background: theme.pageBg }} data-theme={themeId}>
      <style>{themeCssString}</style>
      {/* Header */}
      <header
        className="shrink-0 flex items-center justify-between px-4 py-3 border-b"
        style={{ background: theme.headerBg, borderColor: theme.headerBorder }}
      >
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/document-templates')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              disabled={isViewMode}
              className="text-lg font-semibold bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1 disabled:cursor-default"
              placeholder="Template Name"
            />
            <input
              type="text"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              disabled={isViewMode}
              className="block text-sm text-gray-500 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1 w-full disabled:cursor-default"
              placeholder="Add description..."
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Phase AI: Recreate from uploaded document */}
          {!isViewMode && (
            <>
              <input
                ref={recreateInputRef}
                type="file"
                accept=".pdf,.docx,.doc,.md,.markdown,.txt"
                className="hidden"
                onChange={(e) => { void handleRecreateFile(e.target.files?.[0] ?? null); }}
              />
              <button
                onClick={() => recreateInputRef.current?.click()}
                disabled={recreating}
                data-tour-sections={SECTIONS_TOUR_TARGET.recreate}
                title="Recreate template from an uploaded PDF, Word, Markdown, or text document"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <Upload className="h-4 w-4" />
                {recreating ? 'Recreating\u2026' : 'Recreate'}
              </button>
              {/* G-AI-UX-01: confirm/rename/bind the detected merge-field placeholders. */}
              <RecreatePlaceholderReviewModal
                open={placeholderReview !== null}
                fileName={placeholderReview?.fileName}
                placeholders={placeholderReview?.placeholders ?? []}
                onClose={() => setPlaceholderReview(null)}
                onConfirm={(rename) => {
                  if (Object.keys(rename).length > 0) {
                    setDocumentLayout((prev) => (prev ? renamePlaceholdersInLayout(prev, rename) : prev));
                  }
                  setPlaceholderReview(null);
                }}
              />
            </>
          )}
          {/* Phase 5: Section-editor contextual help + guided tour controls. */}
          <button
            type="button"
            onClick={() => openHelpAt()}
            data-tour-sections={SECTIONS_TOUR_TARGET.help}
            data-testid="open-sections-help"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            <HelpCircle className="h-4 w-4" /> Help
          </button>
          <button
            type="button"
            onClick={startSectionsTour}
            data-testid="start-sections-tour"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            <Compass className="h-4 w-4" /> Tour
          </button>
          <button
            onClick={() => setShowPageSetup((v) => !v)}
            title="Page setup"
            data-tour-sections={SECTIONS_TOUR_TARGET.pageSetup}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              showPageSetup
                ? 'bg-gray-900 text-white border-gray-900'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Settings className="h-4 w-4" /> Page Setup
          </button>
          <button
            onClick={() => setShowPreview(true)}
            data-tour-sections={SECTIONS_TOUR_TARGET.preview}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Eye className="h-4 w-4" />
            Preview
          </button>
          <DocumentLifecycleControls
            status={definition?.status}
            saving={saving}
            publishing={publishing}
            isViewMode={isViewMode}
            isNew={isNew}
            onSave={handleSave}
            onPublish={handlePublish}
            onArchive={handleArchive}
            accentColor={theme.accent}
            saveDataTourSections={SECTIONS_TOUR_TARGET.save}
          />
        </div>
      </header>

      {/* 3-Panel Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Sections (resizable, H3) */}
        <aside
          className="theme-grid shrink-0 border-r overflow-y-auto"
          style={{ width: leftPanelWidth, background: theme.toolbarBg, borderColor: theme.toolbarBorder }}
          data-testid="builder-left-panel"
          data-tour-sections={SECTIONS_TOUR_TARGET.sectionList}
        >
          <div className="p-3 border-b" style={{ borderColor: theme.toolbarBorder }}>
            <div className="text-sm font-semibold text-gray-900">Document elements</div>
            <div className="text-xs text-gray-500">Check to show on the document · click to edit</div>
          </div>
          <div className="p-2 space-y-1" data-testid="activation-list">
            {bandActivationList(documentLayout ?? buildCanvasLayout()).map((entry, idx) => {
              const isActive = entry.id === selectedBandId;
              return (
                <div key={entry.id}>
                  <div
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                      isActive
                        ? 'bg-blue-50 border border-blue-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                    data-testid={`activation-row-${entry.kind}`}
                    onClick={() => { handleSelectBand(entry.id); setShowAdvancedEditor(false); }}
                  >
                    <button
                      type="button"
                      disabled={isViewMode}
                      aria-label={entry.enabled ? `Hide ${entry.label}` : `Show ${entry.label}`}
                      aria-pressed={entry.enabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleBandActivation(entry.id, !entry.enabled);
                      }}
                      data-tour-sections={idx === 0 ? SECTIONS_TOUR_TARGET.sectionToggle : undefined}
                      data-testid={`activation-toggle-${entry.kind}`}
                      className={`p-1 rounded disabled:opacity-50 ${
                        entry.enabled
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {entry.enabled ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    </button>
                    <FileText className={`h-4 w-4 ${entry.enabled ? 'text-gray-600' : 'text-gray-300'}`} />
                    <span className={`text-sm flex-1 ${entry.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                      {entry.label}
                    </span>
                    <ChevronRight className={`h-4 w-4 ${isActive ? 'text-blue-500' : 'text-gray-300'}`} />
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Document Type */}
          <div className="p-3 border-t" style={{ borderColor: theme.toolbarBorder }}>
            <div className="text-xs font-medium text-gray-600 mb-2">Document Type</div>
            <select
              value={config.documentType}
              onChange={(e) => handleDocumentTypeChange(e.target.value as DocumentTypeSelectorValue)}
              disabled={isViewMode}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              <option value="inspectionReport">Inspection Report</option>
              <option value="permit">Permit</option>
              <option value="noticeOfViolation">Notice of Violation</option>
              <option value="letter">Letter</option>
              <option value="certificateOfCompliance">Certificate of Compliance</option>
              <option value="custom">Custom</option>
            </select>
            <div className="mt-3">
              <ReportDataScopeControl
                config={config}
                disabled={isViewMode || config.documentType !== 'inspectionReport'}
                onChange={handleReportDataScopeChange}
              />
            </div>
          </div>
          
          {/* Layout Mode */}
          <div className="p-3 border-t" style={{ borderColor: theme.toolbarBorder }}>
            <div className="text-xs font-medium text-gray-600 mb-2">Layout Mode</div>
            <select
              value={config.layoutMode}
              onChange={(e) => setConfig({ ...config, layoutMode: e.target.value as any })}
              disabled={isViewMode}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              <option value="form">Form (grid-based, like DSE checklist)</option>
              <option value="letter">Letter (narrative, like inspection report)</option>
            </select>
          </div>

          {/* Theme & Branding (collapsible — restores parity with tenant-web; G-THEME-SW-01) */}
          <AccordionSection title="Theme & Branding" storageKey="doc-builder-section-theme" defaultOpen>
          <div className="p-3" style={{ borderColor: theme.toolbarBorder }}>
            <DocumentThemeEditor
              preset={config.themePreset}
              onPresetChange={(id) => setConfig({ ...config, themePreset: id })}
              custom={config.themeCustom}
              onCustomChange={(themeCustom) => setConfig({ ...config, themeCustom })}
              disabled={isViewMode}
            />
          </div>
          </AccordionSection>

          {/* Document Mode — Standard vs Advanced Linked (collapsible — H3) */}
          <AccordionSection title="Advanced" storageKey="doc-builder-section-advanced">
          <div className="p-3" style={{ borderColor: theme.toolbarBorder }}>
            <div className="text-xs font-medium text-gray-600 mb-2">Document Mode</div>
            <select
              value={advancedMode}
              onChange={(e) => {
                const mode = e.target.value as 'standard' | 'advancedLinked';
                setAdvancedMode(mode);
                if (mode === 'advancedLinked') setShowAdvancedTab(true);
              }}
              disabled={isViewMode}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              <option value="standard">Standard</option>
              <option value="advancedLinked">Advanced Linked</option>
            </select>

            {advancedMode === 'advancedLinked' && linkedTemplateName && (
              <div className="mt-2 flex items-center gap-1.5 px-2 py-1.5 bg-blue-50 border border-blue-200 rounded-md">
                <ClipboardList className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                <span className="text-xs text-blue-700 truncate">{linkedTemplateName}</span>
              </div>
            )}

            {advancedMode === 'advancedLinked' && showAdvancedTab && (
              <button
                onClick={() => setShowAdvancedEditor(true)}
                className={`mt-2 w-full text-left px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  showAdvancedEditor
                    ? 'text-blue-800 bg-blue-100 border border-blue-300'
                    : 'text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100'
                }`}
              >
                ⚙ Advanced Data ({advancedFields.length} field{advancedFields.length !== 1 ? 's' : ''})
              </button>
            )}
          </div>
          </AccordionSection>
        </aside>

        {/* Resize handle for the left settings panel (H3) */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize settings panel"
          data-testid="left-panel-resizer"
          onPointerDown={beginResize('left', leftPanelWidth)}
          className="w-1 shrink-0 cursor-col-resize bg-transparent hover:bg-blue-300"
        />

        {/* Center - Section Editor, Canvas Builder, or Advanced Data Editor */}
        <main className="theme-grid flex-1 overflow-auto p-6">
          {/* Phase AI: provenance note — shown when template was recreated from an upload. */}
          {recreatedFrom && (
            <div className="mb-2 text-[11px] text-gray-500 max-w-2xl">
              Recreated from <span className="font-medium">{recreatedFrom.fileName}</span>
              {' '}on {new Date(recreatedFrom.recreatedAt).toLocaleDateString()}
              {' '}({recreatedFrom.parser === 'llamaparse' ? 'LlamaParse' : 'local parser'})
            </div>
          )}
          {/* Phase AI: Squirrel conversational document-agent bar (canvas mode only). */}
          {editMode === 'canvas' && !isViewMode && (
            <div className="mb-3 rounded-lg border border-orange-200 bg-orange-50 p-2 max-w-2xl">
              <div className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-orange-500 shrink-0" />
                <input
                  type="text"
                  value={squirrelInput}
                  onChange={(e) => setSquirrelInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAskSquirrel(); } }}
                  placeholder="Ask Squirrel to change this document (e.g. 'add a signature block and a legal disclaimer')"
                  className="flex-1 px-2 py-1 text-xs border border-orange-200 rounded-md bg-white"
                  disabled={squirrelBusy}
                />
                <button
                  type="button"
                  onClick={() => void handleAskSquirrel()}
                  disabled={squirrelBusy || !squirrelInput.trim()}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-orange-600 text-white disabled:opacity-50"
                >
                  {squirrelBusy ? 'Thinking\u2026' : 'Ask Squirrel'}
                </button>
              </div>
              {squirrelReply && <div className="mt-1.5 text-[11px] text-orange-800">{squirrelReply}</div>}
            </div>
          )}
          {showAdvancedEditor ? (
          <div className="max-w-4xl" data-tour-sections={SECTIONS_TOUR_TARGET.sectionConfig}>
              <AdvancedDataEditor
                fieldCatalog={fieldCatalog}
                advancedFields={advancedFields}
                onAddField={handleAddFieldPlacement}
                onRemoveField={handleRemoveFieldPlacement}
                linkedTemplateName={linkedTemplateName}
                renderAdvancedSection={renderAdvancedSection}
                onRenderAdvancedSectionChange={setRenderAdvancedSection}
                advancedSectionLabel={advancedSectionLabel}
                onAdvancedSectionLabelChange={setAdvancedSectionLabel}
                copiedFieldId={copiedFieldId}
                onCopyToken={(fieldId) => {
                  const token = `{{advField:${fieldId}}}`;
                  navigator.clipboard.writeText(token);
                  setCopiedFieldId(fieldId);
                  setTimeout(() => setCopiedFieldId(null), 1500);
                }}
                isViewMode={isViewMode}
              />
          </div>
          ) : (
            <DocumentCanvasBuilder
              layout={documentLayout ?? buildCanvasLayout()}
              onChange={updateLayout}
              selectedId={selectedBandId}
              onSelectedIdChange={handleSelectBand}
              propertiesPortalTarget={propertiesHost}
              activationDriven
              sampleData={canvasSampleData}
              config={config}
              onConfigChange={setConfig}
              onPickLogo={handlePickLogo}
              resolveLogoThumb={resolveLogoThumb}
              resolveDepartmentLogoThumb={resolveDepartmentLogoThumb}
              resolveDepartmentSecondLogoThumb={resolveDepartmentSecondLogoThumb}
              departmentLogoNote="System preview branding placeholder"
              renderRichTextEditor={({ html, onChange: onRteChange }) => (
                <RichTextEditor content={html} onChange={onRteChange} minHeight={180} />
              )}
            />
          )}
        </main>

        {showPageSetup && editMode === 'canvas' && documentLayout && (
          <div className="theme-grid flex-shrink-0 w-72 border-l border-gray-200 bg-white overflow-y-auto">
            <PageSetupPanel
              page={documentLayout.page}
              onChange={(page) => setDocumentLayout((prev) => prev ? { ...prev, page } : prev)}
              onClose={() => setShowPageSetup(false)}
            />
          </div>
        )}
        {/* Resize handle for the preview pane (H2) */}
        {!previewCollapsed && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize preview"
            data-testid="preview-resizer"
            onPointerDown={beginResize('preview', previewWidth)}
            className="w-1 shrink-0 cursor-col-resize bg-transparent hover:bg-blue-300"
          />
        )}
        {/* Right Panel - Preview (resizable + collapsible, H2; toggle with Ctrl+Shift+P) */}
        {previewCollapsed ? (
          <aside
            className="theme-grid w-10 shrink-0 border-l flex flex-col items-center pt-3"
            style={{ background: theme.toolbarBg, borderColor: theme.toolbarBorder }}
            data-testid="builder-preview-panel"
          >
            <button
              type="button"
              aria-label="Expand pane"
              data-testid="expand-preview"
              onClick={() => setPreviewCollapsed(false)}
              title="Expand pane (Ctrl+Shift+P)"
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
            >
              <PanelRightOpen className="h-4 w-4" />
            </button>
          </aside>
        ) : (
        <aside
          className="theme-grid shrink-0 border-l flex flex-col"
          style={{ width: previewWidth, background: theme.toolbarBg, borderColor: theme.toolbarBorder }}
          data-testid="builder-preview-panel"
          data-tour-sections={SECTIONS_TOUR_TARGET.livePreview}
        >
          {/* Tab strip: Live Preview and the band Properties share this pane (shown alternately). */}
          <div className="shrink-0 flex items-center gap-1 border-b border-gray-200 px-2 pt-1.5">
            <div role="tablist" aria-label="Preview and properties" className="flex flex-1 items-end gap-1">
              <button
                type="button"
                role="tab"
                aria-selected={rightTab === 'preview'}
                data-testid="right-tab-preview"
                onClick={() => setRightTab('preview')}
                className={`rounded-t px-2.5 py-1 text-xs font-medium ${rightTab === 'preview' ? 'bg-white text-gray-900 border border-b-0 border-gray-200' : 'text-gray-500 hover:text-gray-800'}`}
              >
                Live Preview
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={rightTab === 'properties'}
                data-testid="right-tab-properties"
                onClick={() => setRightTab('properties')}
                className={`rounded-t px-2.5 py-1 text-xs font-medium ${rightTab === 'properties' ? 'bg-white text-gray-900 border border-b-0 border-gray-200' : 'text-gray-500 hover:text-gray-800'}`}
              >
                Properties
              </button>
            </div>
            <button
              type="button"
              aria-label="Collapse preview"
              data-testid="collapse-preview"
              onClick={() => setPreviewCollapsed(true)}
              title="Collapse pane (Ctrl+Shift+P)"
              className="mb-0.5 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <PanelRightClose className="h-4 w-4" />
            </button>
          </div>

          {/* Live Preview tab body */}
          <div
            role="tabpanel"
            data-testid="right-tab-preview-panel"
            className={`flex-1 overflow-y-auto p-4 ${rightTab === 'preview' ? '' : 'hidden'}`}
          >
            <div className="mb-2 flex items-center justify-end">
              <select
                aria-label="Preview department"
                data-testid="preview-department-select"
                value={previewDepartmentId}
                onChange={(e) => setPreviewDepartmentId(e.target.value)}
                className="max-w-[55%] rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-300"
                title="Choose a department whose data populates the preview"
              >
                <option value={SAMPLE_PREVIEW_DEPARTMENT_ID}>Sample department</option>
                {previewDepartments.map((d) => (
                  <option key={String(d._id)} value={String(d._id)}>{d.name}</option>
                ))}
              </select>
            </div>
            <div className="text-xs text-gray-500 mb-3">Live preview — reflects current Sections and Canvas edits</div>
            <DocumentLayoutHtmlPreview layout={documentLayout ?? buildCanvasLayout()} renderData={canvasSampleData} scale={0.75} />
          </div>

          {/* Properties tab body — the canvas builder portals its band-properties panel in here. */}
          <div
            role="tabpanel"
            ref={setPropertiesHost}
            data-testid="properties-tab-host"
            className={`min-h-0 flex-1 ${rightTab === 'properties' ? 'flex flex-col' : 'hidden'}`}
          >
            {!selectedBandId && (
              <div className="flex h-full items-center justify-center p-6 text-center text-xs text-gray-400">
                Select an element on the canvas to edit its properties.
              </div>
            )}
          </div>
        </aside>
        )}
      </div>

      {/* Phase 5: Section-editor illustrated help panel, guided tour, and per-section popover. */}
      <BuilderHelpPanel
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        onStartTour={startSectionsTour}
        initialSectionId={helpPanelSection}
      />
      <GuidedTour steps={SECTIONS_TOUR_STEPS} active={tourActive} onComplete={finishTour} />
      {/* G-LOGO-01: shared upload modal used by the canvas logo slots. */}
      <FileUploadModal
        isOpen={logoPickerOpen}
        onClose={handleLogoPickerClose}
        onSuccess={handleLogoUploaded}
      />
      {helpSectionId && (
        <SectionHelpPopover
          label={config.sections.find((s) => s.id === helpSectionId)?.label ?? helpSectionId}
          entry={SECTION_HELP[helpSectionId]}
          anchorRef={sectionHelpAnchorRef}
          open={helpSectionId !== null}
          onClose={() => setHelpSectionId(null)}
          onOpenFullHelp={(section) => { setHelpSectionId(null); openHelpAt(section); }}
        />
      )}
    </div>
  );
}
